import { useState, useEffect, useRef } from 'react';

const FONT_TITLE = "'Ndot55', monospace";
const FONT_MONO  = "'Space Mono', monospace";
const CHAR_POOL  = '0123456789░▒▓';
const MAX_CHARS  = 20;  // accommodate longest project name
const BASE_VW    = 9;   // reference font size in vw
const REF_CHARS  = 10;  // STACYVERSE char count — scaling reference

const WORD_MAP = {
  stacy:      'STACY PILLS',
  pillheads:  'PILL HEADS',
  palette:    'PALETTE',
  repillca:   'RE PILLCA',
  sorrow:     'SORROW OF CHOICE',
  physical:   'THE PILLHEAD',
  cfw_market: 'CFW MARKET',
};

function getWord(hovered, active) {
  const proj = active || hovered;
  return proj ? (WORD_MAP[proj] || 'STACYVERSE') : 'STACYVERSE';
}

function rndChar() {
  return CHAR_POOL[Math.floor(Math.random() * CHAR_POOL.length)];
}

function cellsFromWord(word) {
  return Array.from({ length: MAX_CHARS }, (_, i) => {
    const ch = i < word.length ? word[i] : ' ';
    return { char: ch !== ' ' ? ch : rndChar(), isLetter: ch !== ' ' };
  });
}

/* Compute font size in vw based on total non-space character count */
function computeFontVw(word) {
  const nonSpaceLen = word.replace(/\s/g, '').length;
  const scaleFactor = Math.min(1, REF_CHARS / nonSpaceLen);
  return Math.max(6, BASE_VW * scaleFactor);
}

export default function WordmarkLiving({ hoveredProject, activeProject }) {
  const [cells, setCells]     = useState(() => cellsFromWord('STACYVERSE'));
  const targetRef             = useRef(
    Array.from({ length: MAX_CHARS }, (_, i) => i < 10 ? 'STACYVERSE'[i] : ' ')
  );
  const prevWordRef           = useRef('STACYVERSE');
  const intervalsRef          = useRef([]);
  const timeoutsRef           = useRef([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const h  = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', h);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', h);
  }, []);

  const currentWord = getWord(hoveredProject, activeProject);
  /* On mobile, cap font size smaller so it doesn't dominate the canvas */
  const fontVw      = isMobile
    ? Math.min(computeFontVw(currentWord), 7.5)
    : computeFontVw(currentWord);
  // Subtle top/left bleed — ~6% and ~1% of one character height respectively
  const topOffset   = `-${(fontVw * 0.06).toFixed(2)}vw`;
  const leftOffset  = `-${(fontVw * 0.01).toFixed(2)}vw`;

  /* ─── Word cascade on change ─── */
  useEffect(() => {
    const word = getWord(hoveredProject, activeProject);
    if (word === prevWordRef.current) return;
    prevWordRef.current = word;

    const newTarget = Array.from({ length: MAX_CHARS }, (_, i) =>
      i < word.length ? word[i] : ' '
    );
    targetRef.current = newTarget;

    /* All cells → noise immediately */
    setCells(Array.from({ length: MAX_CHARS }, () => ({ char: rndChar(), isLetter: true })));

    /* Cascade: each cell snaps to its target after a random delay */
    const timers = Array.from({ length: MAX_CHARS }, (_, i) => {
      const delay = Math.random() * 400;
      return setTimeout(() => {
        const ch       = newTarget[i];
        const isLetter = ch !== ' ';
        setCells(prev => {
          const next = [...prev];
          next[i] = { char: isLetter ? ch : rndChar(), isLetter };
          return next;
        });
      }, delay);
    });

    return () => timers.forEach(clearTimeout);
  }, [hoveredProject, activeProject]);

  /* ─── Per-cell glitch: 15% chance per 300–600ms interval ─── */
  useEffect(() => {
    for (let i = 0; i < MAX_CHARS; i++) {
      const interval   = 300 + Math.random() * 300;
      const startDelay = Math.random() * 600;

      const t = setTimeout(() => {
        const id = setInterval(() => {
          const target = targetRef.current[i];
          if (!target || target === ' ') return;
          if (Math.random() >= 0.15) return;

          /* Flash noise */
          setCells(prev => {
            const next = [...prev];
            next[i] = { char: rndChar(), isLetter: true };
            return next;
          });

          /* Snap back after 80ms */
          setTimeout(() => {
            setCells(prev => {
              const next = [...prev];
              const t    = targetRef.current[i];
              next[i] = { char: t !== ' ' ? t : rndChar(), isLetter: t !== ' ' };
              return next;
            });
          }, 80);
        }, interval);

        intervalsRef.current.push(id);
      }, startDelay);

      timeoutsRef.current.push(t);
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];
    };
  }, []);

  /* ─── Build per-line slices from the flat cells array ─── */
  const displayLines = currentWord.split(' ').filter(Boolean);
  let cellOffset = 0;

  return (
    <div
      data-testid="wordmark-living"
      style={{
        position: 'fixed',
        top: topOffset,
        left: leftOffset,
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 10,
        opacity: activeProject ? 0.12 : 1,
        transition: 'opacity 0.2s steps(3)',
      }}
    >
      {/* Transmuting title — Ndot55 dot-matrix, multi-line stacking */}
      <div style={{
        fontFamily: FONT_TITLE,
        fontSize: `${fontVw}vw`,
        lineHeight: 0.88,
        color: '#0D0D0D',
        letterSpacing: '0.02em',
      }}>
        {displayLines.map((lineStr, lineIdx) => {
          const start     = cellOffset;
          const lineCells = cells.slice(start, start + lineStr.length);
          cellOffset     += lineStr.length + 1; // +1 to skip the space separator
          return (
            <div key={lineIdx} style={{ display: 'block', whiteSpace: 'nowrap' }}>
              {lineCells.map((cell, ci) => (
                <span
                  key={ci}
                  style={{
                    display: 'inline-block',
                    opacity: cell.isLetter ? 1 : 0.08,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cell.char}
                </span>
              ))}
            </div>
          );
        })}
      </div>

      {/* Static subline — hidden on mobile to reduce clutter */}
      {!isMobile && (
        <div style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          color: '#0D0D0D',
          opacity: 0.55,
          textTransform: 'uppercase',
          letterSpacing: '0.3em',
          marginTop: 12,
          whiteSpace: 'nowrap',
        }}>
          // PILLHEADS.ART · EST.2021 · ETH · BTC · IRL
        </div>
      )}
    </div>
  );
}
